// Carga masiva de personal desde Excel/CSV. Nómina → Empleados.
// La importación NO escribe empleados: escribe filas en espera, las valida una
// por una y muestra el veredicto. Sólo al aplicar llama a las mismas RPC del
// alta individual, para que ninguna regla del Código del Trabajo se salte.
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  Modal, money, nomRpc, useDialogoEnLinea,
  type ColumnaPlantilla, type ImportCabecera, type ImportFila,
} from './shared';

interface Detalle { cabecera: ImportCabecera | null; filas: ImportFila[] }

// Lector de CSV mínimo pero correcto: respeta comillas y comas dentro de ellas,
// que es exactamente donde fallan los parsers de una línea.
function leerCsv(texto: string): Record<string, string>[] {
  const filas: string[][] = [];
  let fila: string[] = [], campo = '', comillas = false;
  const t = texto.replace(/^\uFEFF/, '');   // BOM que deja Excel al guardar CSV
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (comillas) {
      if (c === '"' && t[i + 1] === '"') { campo += '"'; i++; }
      else if (c === '"') comillas = false;
      else campo += c;
    } else if (c === '"') comillas = true;
    else if (c === ',' || c === ';' || c === '\t') { fila.push(campo); campo = ''; }
    else if (c === '\n') { fila.push(campo); filas.push(fila); fila = []; campo = ''; }
    else if (c !== '\r') campo += c;
  }
  if (campo !== '' || fila.length) { fila.push(campo); filas.push(fila); }
  if (filas.length < 2) return [];
  const cab = filas[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'));
  return filas.slice(1)
    .filter((f) => f.some((v) => v.trim() !== ''))
    .map((f) => Object.fromEntries(cab.map((h, i) => [h, (f[i] ?? '').trim()])));
}

const COLOR: Record<string, string> = {
  ok: 'text-ok', aviso: 'text-pendiente', error: 'text-destructive',
  aplicada: 'text-ok', fallida: 'text-destructive',
};

export function ImportarPersonalModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const fileRef = useRef<HTMLInputElement>(null);
  const [id, setId] = useState('');

  const { data: plantilla = [] } = useQuery<ColumnaPlantilla[]>({
    queryKey: ['nom-import-plantilla'],
    queryFn: () => nomRpc<ColumnaPlantilla[]>('nom_import_plantilla'),
  });
  const { data: det } = useQuery<Detalle>({
    queryKey: ['nom-import', id],
    queryFn: () => nomRpc<Detalle>('nom_import', { _id: id }),
    enabled: !!id,
  });

  const cargar = useMutation({
    mutationFn: (v: { archivo: string; filas: Record<string, string>[] }) =>
      nomRpc<Detalle>('nom_import_cargar', { _data: { archivo: v.archivo, filas: v.filas } }),
    onSuccess: (d) => {
      setId(d.cabecera?.id ?? '');
      toast.success(`${d.cabecera?.n_filas} fila(s) leídas: ${d.cabecera?.n_error} con error`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const aplicar = useMutation({
    mutationFn: (confirmar: boolean) =>
      nomRpc<{ aplicada: boolean; mensaje: string }>('nom_import_aplicar', { _id: id, _confirmar: confirmar }),
    onSuccess: async (r) => {
      if (r.aplicada) {
        toast.success(r.mensaje);
        void qc.invalidateQueries({ queryKey: ['nom-empleados'] });
        void qc.invalidateQueries({ queryKey: ['nom-contratos'] });
        void qc.invalidateQueries({ queryKey: ['nom-import'] });
      } else if (await dlg.confirmar('¿Crear las altas?', { detalle: r.mensaje, aceptar: 'Crear' })) {
        aplicar.mutate(true);
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const bajarPlantilla = () => {
    const cab = plantilla.map((c) => c.col).join(',');
    const blob = new Blob(['﻿' + cab + '\n'], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'plantilla-personal.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onArchivo = async (f: File) => {
    const filas = leerCsv(await f.text());
    if (filas.length === 0) { toast.error('El archivo no tiene filas de datos'); return; }
    cargar.mutate({ archivo: f.name, filas });
  };

  const cab = det?.cabecera ?? null;
  const filas = det?.filas ?? [];
  const aplicada = cab?.estado === 'aplicada';

  return (
    <Modal title="Cargar personal desde un archivo" onClose={onClose} wide>
      <div className="space-y-4">
        {dlg.ui}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={bajarPlantilla}>
            <Download className="w-4 h-4 mr-1" /> Plantilla CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onArchivo(f); e.target.value = ''; }} />
          <Button size="sm" isLoading={cargar.isPending} onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4 mr-1" /> Elegir archivo
          </Button>
          <span className="text-xs text-muted-foreground">
            Guarda el Excel como CSV. Nada se crea hasta que revises y apliques.
          </span>
        </div>

        {!cab && plantilla.length > 0 && (
          <div className="rounded-md border border-border bg-muted/20 p-3 text-xs">
            <div className="flex items-center gap-2 mb-2 font-medium">
              <FileSpreadsheet className="w-4 h-4" /> Columnas que entiende
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
              {plantilla.map((c) => (
                <div key={c.col}>
                  <span className="font-mono">{c.col}</span>
                  {c.req && <span className="text-destructive"> *</span>}
                  <span className="text-muted-foreground"> — {c.titulo}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {cab && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Filas</div><div className="text-lg font-semibold tabular-nums">{cab.n_filas}</div></div>
              <div><div className="text-xs text-muted-foreground">Sin problemas</div><div className="text-lg font-semibold tabular-nums text-ok">{cab.n_ok}</div></div>
              <div><div className="text-xs text-muted-foreground">Con aviso</div><div className="text-lg font-semibold tabular-nums text-pendiente">{cab.n_aviso}</div></div>
              <div><div className="text-xs text-muted-foreground">Se rechazan</div><div className="text-lg font-semibold tabular-nums text-destructive">{cab.n_error}</div></div>
              <div><div className="text-xs text-muted-foreground">Creadas</div><div className="text-lg font-semibold tabular-nums">{cab.n_aplicadas}</div></div>
            </div>

            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Estado</th>
                  <th className="py-2 pr-2">Identificación</th><th className="py-2 pr-2">Nombre</th>
                  <th className="py-2 pr-2 text-right">Sueldo</th><th className="py-2 pr-2">Qué pasa</th>
                </tr></thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.id} className="border-b last:border-0 align-top">
                      <td className="py-1.5 pr-2 tabular-nums text-muted-foreground">{f.linea}</td>
                      <td className={`py-1.5 pr-2 ${COLOR[f.estado] ?? ''}`}>
                        {f.estado === 'error' || f.estado === 'fallida'
                          ? <XCircle className="w-4 h-4 inline -mt-0.5" />
                          : f.estado === 'aviso' ? <AlertTriangle className="w-4 h-4 inline -mt-0.5" />
                          : <CheckCircle2 className="w-4 h-4 inline -mt-0.5" />}
                        <span className="ml-1 text-xs">{f.estado}</span>
                      </td>
                      <td className="py-1.5 pr-2 font-mono">{f.identificacion}</td>
                      <td className="py-1.5 pr-2">{f.razon_social}</td>
                      <td className="py-1.5 pr-2 text-right font-mono tabular-nums">
                        {f.sueldo ? money(Number(f.sueldo)) : '—'}
                      </td>
                      <td className="py-1.5 pr-2 text-xs">
                        {f.errores?.map((e, i) => <div key={`e${i}`} className="text-destructive">{e}</div>)}
                        {f.avisos?.map((a, i) => <div key={`a${i}`} className="text-pendiente">{a}</div>)}
                        {f.mensaje && <div className="text-destructive">{f.mensaje}</div>}
                        {!f.errores?.length && !f.avisos?.length && !f.mensaje && <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!aplicada && (
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>Cerrar sin crear nada</Button>
                <Button isLoading={aplicar.isPending} disabled={cab.n_ok + cab.n_aviso === 0}
                  onClick={() => aplicar.mutate(false)}>
                  Crear {cab.n_ok + cab.n_aviso} alta(s)
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
